import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, Shield, BarChart3, Sparkles, Megaphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export type CookieCategory = "essential" | "performance" | "functional" | "advertising";

export interface CookiePreferences {
  essential: true;
  performance: boolean;
  functional: boolean;
  advertising: boolean;
  timestamp: string;
  version: number;
}

const STORAGE_KEY = "klikan_cookie_consent";
const VERSION = 1;

const DEFAULT_PREFS: CookiePreferences = {
  essential: true,
  performance: false,
  functional: false,
  advertising: false,
  timestamp: "",
  version: VERSION,
};

export function getCookiePreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setCookiePreferences(prefs: Omit<CookiePreferences, "timestamp" | "version" | "essential">) {
  const next: CookiePreferences = {
    essential: true,
    performance: prefs.performance,
    functional: prefs.functional,
    advertising: prefs.advertising,
    timestamp: new Date().toISOString(),
    version: VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: next }));
  return next;
}

export function openCookieSettings() {
  window.dispatchEvent(new Event("open-cookie-settings"));
}

const CATEGORIES: Array<{
  key: CookieCategory;
  icon: typeof Shield;
  title: string;
  desc: string;
  locked?: boolean;
}> = [
  {
    key: "essential",
    icon: Shield,
    title: "Essentiels",
    desc: "Indispensables au fonctionnement du site (session, sécurité, préférences de consentement). Ne peuvent pas être désactivés.",
    locked: true,
  },
  {
    key: "performance",
    icon: BarChart3,
    title: "Performance",
    desc: "Mesure d'audience anonymisée pour comprendre comment le site est utilisé et l'améliorer.",
  },
  {
    key: "functional",
    icon: Sparkles,
    title: "Fonctionnalités",
    desc: "Mémorisent vos préférences (langue, affichage) pour une expérience personnalisée.",
  },
  {
    key: "advertising",
    icon: Megaphone,
    title: "Publicité",
    desc: "Permettent de mesurer l'efficacité de nos campagnes et de proposer du contenu pertinent.",
  },
];

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [prefs, setPrefs] = useState({
    performance: false,
    functional: false,
    advertising: false,
  });
  const bannerRef = useRef<HTMLDivElement>(null);
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const existing = getCookiePreferences();
    if (!existing) {
      setShowBanner(true);
    } else {
      setPrefs({
        performance: existing.performance,
        functional: existing.functional,
        advertising: existing.advertising,
      });
    }
    const openHandler = () => {
      triggerRef.current = (document.activeElement as HTMLElement) ?? null;
      const cur = getCookiePreferences();
      if (cur) {
        setPrefs({
          performance: cur.performance,
          functional: cur.functional,
          advertising: cur.advertising,
        });
      }
      setShowDialog(true);
    };
    window.addEventListener("open-cookie-settings", openHandler);
    return () => window.removeEventListener("open-cookie-settings", openHandler);
  }, []);

  // Auto-focus accept button when banner mounts so keyboard users land on a clear action
  useEffect(() => {
    if (showBanner) {
      const t = window.setTimeout(() => acceptBtnRef.current?.focus(), 350);
      return () => window.clearTimeout(t);
    }
  }, [showBanner]);

  // Restore focus to the trigger after closing the preferences dialog
  const handleDialogOpenChange = (open: boolean) => {
    setShowDialog(open);
    if (!open && triggerRef.current) {
      const el = triggerRef.current;
      window.setTimeout(() => el.focus?.(), 0);
      triggerRef.current = null;
    }
  };

  const acceptAll = () => {
    setCookiePreferences({ performance: true, functional: true, advertising: true });
    setPrefs({ performance: true, functional: true, advertising: true });
    setShowBanner(false);
    setShowDialog(false);
  };

  const rejectAll = () => {
    setCookiePreferences({ performance: false, functional: false, advertising: false });
    setPrefs({ performance: false, functional: false, advertising: false });
    setShowBanner(false);
    setShowDialog(false);
  };

  const saveChoices = () => {
    setCookiePreferences(prefs);
    setShowBanner(false);
    setShowDialog(false);
  };

  return (
    <>
      {/* Banner — non-modal region, focusable actions */}
      {showBanner && (
        <div
          ref={bannerRef}
          role="region"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
          className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-300"
        >
          <div className="mx-auto max-w-5xl bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-5 sm:p-6 lg:p-7">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 id="cookie-banner-title" className="text-base sm:text-lg font-semibold text-foreground">
                    Nous respectons votre vie privée
                  </h2>
                  <p id="cookie-banner-desc" className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    Nous utilisons des cookies pour assurer le bon fonctionnement du site, mesurer l'audience et améliorer votre expérience. Vous pouvez accepter, refuser ou personnaliser vos choix à tout moment.{" "}
                    <Link to="/cookies" className="underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                      En savoir plus
                    </Link>
                    .
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" role="group" aria-label="Actions de consentement aux cookies">
                    <Button ref={acceptBtnRef} onClick={acceptAll} className="sm:order-3">
                      Tout accepter
                    </Button>
                    <Button onClick={rejectAll} variant="outline" className="sm:order-2">
                      Tout refuser
                    </Button>
                    <Button
                      onClick={() => {
                        triggerRef.current = bannerRef.current;
                        setShowDialog(true);
                      }}
                      variant="ghost"
                      className="sm:order-1 sm:mr-auto gap-2"
                      aria-haspopup="dialog"
                      aria-expanded={showDialog}
                      aria-controls="cookie-preferences-dialog"
                    >
                      <Settings2 className="h-4 w-4" aria-hidden="true" />
                      Personnaliser
                    </Button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={rejectAll}
                  aria-label="Refuser tous les cookies non essentiels et fermer la bannière"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences dialog */}
      <Dialog open={showDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          id="cookie-preferences-dialog"
          aria-labelledby="cookie-prefs-title"
          aria-describedby="cookie-prefs-desc"
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle id="cookie-prefs-title" className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
              Préférences de cookies
            </DialogTitle>
            <DialogDescription id="cookie-prefs-desc">
              Choisissez les catégories de cookies que vous autorisez. Vous pouvez modifier ce choix à tout moment depuis le pied de page.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-3 mt-2 list-none p-0" aria-label="Catégories de cookies">
            {CATEGORIES.map(({ key, icon: Icon, title, desc, locked }) => {
              const checked = key === "essential" ? true : prefs[key as keyof typeof prefs];
              const descId = `cookie-cat-${key}-desc`;
              return (
                <li
                  key={key}
                  className="flex items-start gap-4 p-4 rounded-lg border border-border bg-muted/30"
                >
                  <div className="h-9 w-9 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center" aria-hidden="true">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                      <Switch
                        checked={checked}
                        disabled={locked}
                        onCheckedChange={(v) => {
                          if (locked) return;
                          setPrefs((p) => ({ ...p, [key]: v }));
                        }}
                        aria-label={`Activer les cookies « ${title} »`}
                        aria-describedby={descId}
                      />
                    </div>
                    <p id={descId} className="mt-1 text-xs text-muted-foreground leading-relaxed">{desc}</p>
                    {locked && (
                      <p className="mt-1 text-[11px] uppercase tracking-wider text-primary/80 font-medium">
                        Toujours actifs
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <Button variant="ghost" onClick={rejectAll} className="sm:mr-auto">
              Tout refuser
            </Button>
            <Button variant="outline" onClick={saveChoices}>
              Enregistrer mes choix
            </Button>
            <Button onClick={acceptAll}>Tout accepter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
